// src/pages/relaxation/ViewRelaxationPage.jsx
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Skeleton,
  Stack,
} from '@mui/material';
import {
  SpaOutlined as SpaIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MicNone as MicIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PutObjectCommand } from '@aws-sdk/client-s3';
import { v4 as uuidv4 } from 'uuid';
import s3awsConfig from 'utils/aws-config';

import CreateRelaxationDialog from '../../components/openDialog/CreateRelaxationDialog';
import EditRelaxationDialog from '../../components/openDialog/EditRelaxationDialog';
import RecordAudioDialog from '../../components/openDialog/RecordAudioDialog';
import { getRelaxations } from '../../services/relaxation/getRelaxation';
import { createRelaxation } from '../../services/relaxation/createRelaxation';
import { updateRelaxation } from '../../services/relaxation/updateRelaxation';
import { deleteRelaxation } from '../../services/relaxation/deleteRelaxation';

function ViewRelaxation() {
  const { courseid } = useParams();
 const roleid = Number(localStorage.getItem("roleid"));
const studentid = localStorage.getItem("studentid");

const parsedStudentId =
  roleid === 2 && studentid ? Number(studentid) : undefined;

  const queryClient = useQueryClient();

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRelaxation, setSelectedRelaxation] = useState(null);
  const [relaxationToDelete, setRelaxationToDelete] = useState(null);
  const [preFillFromRecord, setPreFillFromRecord] = useState(null);

  // ✅ Fixed Query
 const { data, isLoading, error } = useQuery({
  queryKey: ['relaxations', courseid, parsedStudentId],
  queryFn: () =>
    getRelaxations({courseid : Number(courseid), studentid:  parsedStudentId}),
  enabled: !!courseid,
});

const relaxationdata = data?.data || [];
console.log("❤️❤️❤️:", relaxationdata);

  const uploadToS3 = async (fileOrBlob) => {
    try {
      const originalExt = fileOrBlob.name
        ? fileOrBlob.name.split('.').pop() || 'webm'
        : 'webm';

      const fileKey = `relaxation-audio/${uuidv4()}.${originalExt}`;
      const contentType = fileOrBlob.type || 'audio/webm';

      const arrayBuffer = await fileOrBlob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      const command = new PutObjectCommand({
        Bucket: 'roacs-bucket',
        Key: fileKey,
        Body: uint8Array,
        ContentType: contentType,
      });

      await s3awsConfig.send(command);

      return `https://roacs-bucket.s3.ap-south-1.amazonaws.com/${fileKey}`;
    } catch (error) {
      console.error('S3 upload error:', error);
      throw new Error('Failed to upload audio to S3');
    }
  };

  // ✅ Create
const addMutation = useMutation({
  mutationFn: createRelaxation,
  onSuccess: (newRelaxation) => {
    // 1️⃣ Immediately update the cached data
    queryClient.setQueryData(['relaxations', courseid, parsedStudentId], (oldData) => {
      const existing = oldData?.data || [];
      return { ...oldData, data: [newRelaxation, ...existing] }; // add new on top
    });

    // 2️⃣ Also refetch to make sure backend sync is correct
    queryClient.invalidateQueries({
      queryKey: ['relaxations', courseid, parsedStudentId],
    });

    toast.success('Relaxation added successfully');
    setCreateOpen(false);
    setPreFillFromRecord(null);
  },
  onError: (err) => {
    toast.error(err?.response?.data?.message || 'Failed to add relaxation');
  },
});

  const handleRelaxationCreated = async (formData) => {
    if (!formData) return;

    let audioUrl = '';

    if (formData.audioFile instanceof Blob || formData.audioFile instanceof File) {
      const toastId = toast.loading('Uploading audio...');
      try {
        audioUrl = await uploadToS3(formData.audioFile);
        toast.dismiss(toastId);
        toast.success('Audio uploaded');
      } catch {
        toast.dismiss(toastId);
        toast.error('Upload failed');
        return;
      }
    }

    addMutation.mutate({
      trackName: formData.trackName || formData.name || '',
      shortDescription: formData.shortDescription || formData.description || '',
      category: formData.category || '',
      duration: formData.duration || '',
      instructions: formData.instructions || '',
      benefits: formData.benefits || [],
      audioFile: audioUrl,
      courseId: Number(courseid),
    });
  };

  // ✅ Edit
  const editMutation = useMutation({
    mutationFn: ({ id, data }) => updateRelaxation(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['relaxations', courseid, parsedStudentId],
      });
      toast.success('Updated');
      setEditOpen(false);
    },
  });

  // ✅ Delete
  const deleteMutation = useMutation({
    mutationFn: deleteRelaxation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['relaxations', courseid, parsedStudentId],
      });
      toast.success('Deleted successfully');
      setDeleteDialogOpen(false);
      setRelaxationToDelete(null);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete');
    },
  });

  const handleRecordedUpload = (blob, name, duration) => {
    setPreFillFromRecord({
      audioFile: blob,
      name: name || `Recording ${new Date().toLocaleDateString()}`,
      duration: duration || '',
    });
    setRecordOpen(false);
    setCreateOpen(true);
  };

  return (
    <Box sx={{ p: 4, backgroundColor: '#ffffff', minHeight: '100vh' }}>
      {/* ----------- UI REMAINS COMPLETELY UNCHANGED BELOW ----------- */}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 4 }}>
        <Button
          variant="contained"
          startIcon={<MicIcon />}
          onClick={() => setRecordOpen(true)}
          sx={{
            color: 'red',
            backgroundColor: 'white',
            border: '2px solid red',
            '&:hover': { backgroundColor: 'red', color: 'white' },
          }}
        >
          Record Your Audio
        </Button>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ backgroundColor: '#3b66ff', '&:hover': { backgroundColor: '#2a55e0' } }}
        >
          Add New Relaxation
        </Button>
      </Box>

      {isLoading && (
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={250} sx={{ borderRadius: 4 }} />
            </Grid>
          ))}
        </Grid>
      )}

      {!isLoading && error && (
        <Typography color="error">Failed to load data</Typography>
      )}

      {!isLoading && !error && relaxationdata.length === 0 && (
        <Stack alignItems="center" spacing={2} sx={{ mt: 10, color: '#999' }}>
          <SpaIcon sx={{ fontSize: 60 }} />
          <Typography variant="h6">No relaxation audios yet</Typography>
        </Stack>
      )}

      {!isLoading && !error && relaxationdata.length > 0 && (
        <Grid container spacing={3}>
          {relaxationdata.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card sx={{ borderRadius: '16px', border: '1px solid #f0f0f0', height: '100%' }}>
                <Box sx={{ p: 2, backgroundColor: '#f4f6f8' }}>
                  {item.audio_file ? (
                    <audio controls src={item.audio_file} style={{ width: '100%', height: '36px' }} />
                  ) : (
                    <Typography variant="caption" align="center" sx={{ color: '#999', width: '100%', display: 'block' }}>
                      No audio
                    </Typography>
                  )}
                </Box>

                <CardContent>
                  <Typography fontWeight={800}>{item.track_name || 'Untitled'}</Typography>
                  <Typography variant="body2" color="#666" mb={2}>
                    {item.short_description || '—'}
                  </Typography>
                  <Typography sx={{ display: 'flex', gap: 1 }}>
                    <span style={{ color: 'gray', fontSize: '0.875rem' }}>Duration:</span>
                    {item.duration || '—'}
                  </Typography>
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <IconButton onClick={() => { setSelectedRelaxation(item); setEditOpen(true); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    color="error"
                    onClick={() => {
                      setRelaxationToDelete(item);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <RecordAudioDialog
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
        onUpload={handleRecordedUpload}
      />

      <CreateRelaxationDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setPreFillFromRecord(null);
        }}
        onSave={handleRelaxationCreated}
        courseId={courseid}
        initialValues={preFillFromRecord}
      />

      <EditRelaxationDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        relaxation={selectedRelaxation}
        onSave={(data) =>
          editMutation.mutate({ id: selectedRelaxation.id, data })
        }
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Relaxation?</DialogTitle>
        <DialogContent>
          <DialogContentText>This action cannot be undo.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() =>
              deleteMutation.mutate({ id: relaxationToDelete?.id })
            }
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ViewRelaxation;